import { downloadXlsx, s as xs, num2 } from "./xlsxExport";
import { htmlToPdfBlob } from "../pdfRenderer";

/* ============================================================================
 * Grower Audit Report — preview window (HTML, browser-print to PDF) +
 * Excel export. Used by both the shipper portal and the admin (both
 * surface the same V2 grower form via ShipperGrowers).
 *
 * Public API:
 *   buildGrowerAuditHTML(grower, ctx)   → full HTML doc as a string
 *   openGrowerAuditPreview(grower, ctx, { print })
 *   downloadGrowerAuditXLSX(grower, ctx)
 *
 * `ctx` carries the cross-cutting data the report needs:
 *   { certifications, supplier, lang }
 *
 * Compliance status mirrors the same evaluator the form / list use
 * (doc-based: each required cert needs an uploaded document on a
 * matching certification record). We re-implement it here instead of
 * importing from ShipperGrowers to keep this file standalone and the
 * import graph one-way (ShipperGrowers → growerAudit, never the other
 * way).
 * ============================================================================ */

const NAVY = "#1B3A4B";
const GREEN = "#059669";
const RED = "#DC2626";

function escHtml(s) {
  if (s === undefined || s === null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
function fmtDateShort(d) {
  if (!d) return "";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });
  } catch (e) { return String(d); }
}

function growerFullName(g) {
  if (!g) return "";
  if (g.personType === "moral") return g.razonSocial || "";
  return [g.nombre, g.apPaterno, g.apMaterno].filter(Boolean).join(" ");
}
function seasonText(g) {
  if (!g || (!g.seasonYearFrom && !g.seasonYearTo)) return "";
  return (g.seasonYearFrom || "?") + "-" + (g.seasonYearTo || "?");
}
function fullAddress(r) {
  /* Structured address fields with the legacy free-text fallback so
     pre-V2 rancho records still render something. */
  if (!r) return "";
  if (!r.street && !r.municipio && !r.city && !r.state && !r.zip && !r.country) {
    return r.address || "";
  }
  return [r.street, r.municipio, r.city, r.state, r.zip, r.country].filter(Boolean).join(", ");
}

/* 2026-09-28 (Arturo: "capture un nuevo grower... con las nuevas
   reglas de compliance, cumple con lo necesario sin embargo mira el
   audit report como muestra missing docs... en pantalla se ve
   completo pero en preview marca los que no son obligatorios como
   faltantes") — this file re-implements the same required/missing
   evaluator ShipperGrowers.js uses (by design, see file header), so
   it also needed its own copy of the 2 exemptions already applied
   there: Auditing Report is no longer required on organic certs, and
   Corrective Action Report is satisfied by a perfect prelimScore (the
   initial audit, before correction) even with no document uploaded. */
function requiredCertsFor(grower, lang) {
  /* The base food-safety set is always required; organic adds one. */
  var ES = lang === "es";
  var items = [
    { key: "safety",     label: ES ? "Certificado de Inocuidad"      : "Food Safety Certificate" },
    { key: "audit",      label: ES ? "Reporte Final de Auditoría"    : "Final Audit Report" },
    { key: "corrective", label: ES ? "Reporte de Acciones Correctivas" : "Corrective Action Report" },
    { key: "fsma",       label: ES ? "Reporte FSMA"                  : "FSMA Summary Report" },
  ];
  var hasOrganic = (grower && grower.products || []).some(function (p) { return p.category === "Organic"; });
  if (hasOrganic) {
    items.push({ key: "organic", label: ES ? "Certificado Orgánico Vigente" : "Valid Organic Certificate" });
  }
  return items;
}

function evaluateCompliance(grower, certifications) {
  var growerCertId = "grower-" + ((grower && grower.id) || "");
  var ownCerts = (certifications || []).filter(function (c) { return c.entityId === growerCertId; });
  var today = new Date(); today.setHours(0, 0, 0, 0);
  var active = function (c) {
    var end = c.anniversaryDate || c.validTo;
    if (!end) return true;
    return new Date(end) >= today;
  };
  var hasDoc = function (certType, docTypeName) {
    return ownCerts.some(function (c) {
      if (c.certType !== certType) return false;
      if (!active(c)) return false;
      var docs = Array.isArray(c.documents) ? c.documents : [];
      return docs.some(function (d) {
        if (!d || !d.docType) return false;
        return String(d.docType).toLowerCase() === String(docTypeName).toLowerCase();
      });
    });
  };
  var hasPerfectPrelimScore = ownCerts.some(function (c) {
    return c.certType === "foodSafety" && active(c) && parseFloat(c.prelimScore) === 100;
  });
  return {
    safety:       hasDoc("foodSafety", "Certificate"),
    audit:        hasDoc("foodSafety", "Final Audit Report"),
    corrective:   hasDoc("foodSafety", "Corrective Action Report") || hasPerfectPrelimScore,
    fsma:         hasDoc("foodSafety", "FSMA Summary Report"),
    organic:      hasDoc("organic",    "Certificate"),
    organicAudit: hasDoc("organic",    "Auditing Report"),
    ownCerts: ownCerts,
  };
}

/* Flatten every uploaded document across this grower's certifications
   into a single table row list — what the auditor sees on file. */
/* 2026-09-28 (Arturo: "el Audit Report — Grower no muestra el numero
   de certificacion cuando es de food safety, debe tomarlo del
   PrimusGFS Audit # / GGN") — a food-safety cert's real identifier
   never lives in `certNumber` (that field is only ever populated for
   organic certs, assigned by the certifier) — it lives in
   `auditNumber` (Primus) or `ggn`/`ggCode` (GlobalGAP), per scheme.
   Same scheme-aware resolution CertificationRegistry.js's own
   displayCertNumber() already defines, just not wired into this
   report. Cert numbers migrated from the legacy spreadsheet were
   written as the literal "UNKNOWN" when the source row had no number
   — treated as missing data, an em-dash renders instead of leaking
   migration noise. */
function realCertNumber(c) {
  var raw = "";
  if (c && c.certType === "foodSafety" && c.scheme === "globalgap") raw = c.ggn || c.ggCode || "";
  else if (c && c.certType === "foodSafety" && c.scheme === "primus") raw = c.auditNumber || "";
  else raw = (c && c.certNumber) || "";
  var n = String(raw).trim();
  if (!n) return "";
  if (n.toUpperCase() === "UNKNOWN") return "";
  return n;
}

/* Map the certification's subtype (organic / food-safety operation
   category) to a printable label for the audit report. Mirrors the
   keys used in CertificationRegistry.T.{organicSubs, fsSubs}. */
function operationTypeLabel(cert) {
  if (!cert) return "";
  var st = String(cert.subtype || "").toLowerCase();
  var map = {
    crops:           "Crops",
    handler_trader:  "Handler (Trader/Broker)",
    handler_cooling: "Handler (Cooling/Storage/Distribution)",
    farm:            "Farm",
    harvest_crew:    "Harvest Crew",
    cooling:         "Cooling/Cold Storage",
    other:           "Other",
  };
  return map[st] || cert.subtype || "";
}

/* FIX 55 — commodities the cert covers. Pulls from the cert's
   products array (used by organic certs) and falls back to the
   per-cert productName field. Deduplicated. */
function commoditiesForCert(cert) {
  var set = {};
  (cert && Array.isArray(cert.products) ? cert.products : []).forEach(function (p) {
    var n = (p && (p.productName || p.product)) || "";
    if (n) set[n] = true;
  });
  if (Object.keys(set).length === 0 && cert && cert.productName) set[cert.productName] = true;
  return Object.keys(set);
}

function uploadedDocsForGrower(grower, certifications) {
  var growerCertId = "grower-" + ((grower && grower.id) || "");
  var rows = [];
  (certifications || []).forEach(function (c) {
    if (c.entityId !== growerCertId) return;
    var docs = Array.isArray(c.documents) ? c.documents : [];
    /* FIX 53 — pull cert_number from the PARENT certification once
       and stamp it onto every document row instead of asking each
       document for its own number (documents never carried one). */
    var parentCertNumber = realCertNumber(c);
    var parentOpType = operationTypeLabel(c);
    var parentCommodities = commoditiesForCert(c).join(", ");
    docs.forEach(function (d) {
      if (!d) return;
      rows.push({
        certType:    c.certType || "",
        certNumber:  parentCertNumber,
        certifier:   c.certifier === "__other__" ? (c.certifierOther || "") : (c.certifier || ""),
        operationType: parentOpType,
        commodities: parentCommodities,
        docType:     d.docType || "",
        docName:     d.name || "",
        /* 2026-09-28 (Arturo: "al subir un mrl, debe pedirme un Lab
           Test ID# ... para que al generar el audit report lo muestre
           ahi") — captured per-document on upload, see
           CertificationRegistry.js's TypedDocumentSection. */
        labTestId:   d.labTestId || "",
        /* Sample date (Arturo, same turn: "el mrl debe tener una
           fecha del muestreo... para captura"). */
        sampleDate:  d.sampleDate || "",
        uploadedAt:  c.updated_at || c.created_at || "",
      });
    });
  });
  return rows;
}

/* ============================================================================
 * HTML builder
 * ============================================================================ */
export function buildGrowerAuditHTML(grower, ctx) {
  ctx = ctx || {};
  var certifications = ctx.certifications || [];
  var supplier = ctx.supplier || null;
  var lang = ctx.lang === "en" ? "en" : "es";
  var ES = lang === "es";
  var t = ES ? {
    title: "Reporte de Auditoría — Productor",
    docSub: "Audit Report — Grower",
    sec_identity: "Identidad",
    sec_season: "Temporada",
    sec_contact: "Contacto",
    sec_products: "Productos que Entrega",
    sec_ranchos: "Ranchos",
    sec_certs: "Certificaciones Requeridas",
    sec_docs: "Documentos Subidos",
    field_personType: "Tipo de persona", field_name: "Nombre",
    field_code: "Código del Productor", field_shipper: "Exportador Vinculado",
    field_email: "Correo", field_phone: "Teléfono",
    field_status: "Estado", field_active: "Activo", field_inactive: "Inactivo",
    col_product: "Producto", col_category: "Categoría",
    col_compliance: "Cumplimiento",
    col_rancho: "Predio", col_address: "Dirección", col_hectares: "Hectáreas",
    col_cert: "Certificación", col_certNumber: "No. Cert", col_certifier: "Certificadora",
    col_operation: "Tipo de Operación", col_commodity: "Commodity",
    col_doc: "Documento", col_docType: "Tipo", col_docName: "Archivo",
    col_labTestId: "No. ID Lab Test", col_sampleDate: "Fecha de Muestreo",
    col_uploaded: "Subido el",
    ok: "Cumplido", missing: "Falta",
    fisica: "Persona Física", moral: "Persona Moral",
    organic: "Orgánico", conventional: "Convencional",
    noProducts: "Sin productos registrados.",
    noRanchos: "Sin ranchos registrados.",
    noDocs: "Sin documentos cargados.",
    generated: "Generado el",
  } : {
    title: "Audit Report — Grower",
    docSub: "Reporte de Auditoría — Productor",
    sec_identity: "Identity",
    sec_season: "Season",
    sec_contact: "Contact",
    sec_products: "Products Delivered",
    sec_ranchos: "Ranchos",
    sec_certs: "Required Certifications",
    sec_docs: "Uploaded Documents",
    field_personType: "Person type", field_name: "Name",
    field_code: "Producer code", field_shipper: "Linked shipper",
    field_email: "Email", field_phone: "Phone",
    field_status: "Status", field_active: "Active", field_inactive: "Inactive",
    col_product: "Product", col_category: "Category",
    col_compliance: "Compliance",
    col_rancho: "Rancho", col_address: "Address", col_hectares: "Hectares",
    col_cert: "Certification", col_certNumber: "Cert #", col_certifier: "Certifier",
    col_operation: "Operation Type", col_commodity: "Commodity",
    col_doc: "Document", col_docType: "Type", col_docName: "File",
    col_labTestId: "Lab Test ID#", col_sampleDate: "Sample Date",
    col_uploaded: "Uploaded on",
    ok: "OK", missing: "Missing",
    fisica: "Individual", moral: "Business",
    organic: "Organic", conventional: "Conventional",
    noProducts: "No products on file.",
    noRanchos: "No ranchos on file.",
    noDocs: "No uploaded documents.",
    generated: "Generated",
  };

  var compliance = evaluateCompliance(grower, certifications);
  var required = requiredCertsFor(grower, lang);
  var docs = uploadedDocsForGrower(grower, certifications);
  var phoneStr = [grower && grower.phoneCountry ? "+" + grower.phoneCountry : "", grower && grower.phoneArea, grower && grower.phoneNumber].filter(Boolean).join(" ");
  var fullName = growerFullName(grower);
  var producerCode = (grower && grower.producerCode) || "";
  var statusLabel = (grower && grower.isActive !== false) ? t.field_active : t.field_inactive;
  var shipperName = supplier ? (supplier.shortName || supplier.fullName || "") : "";
  var nowStr = new Date().toLocaleString();

  /* Per-product compliance for the products table — CV requires 4 base
     docs, OG requires the same 4 + organic (Auditing Report no longer
     required, see requiredCertsFor above). */
  var cvOk = compliance.safety && compliance.audit && compliance.corrective && compliance.fsma;
  var ogOk = cvOk && compliance.organic;
  var pillRed   = 'background:rgba(220,38,38,0.12);color:' + RED + ';border:1px solid rgba(220,38,38,0.30)';
  var pillGreen = 'background:rgba(16,185,129,0.12);color:' + GREEN + ';border:1px solid rgba(16,185,129,0.30)';

  var h = '';
  h += '<!DOCTYPE html><html><head><meta charset="utf-8"><title>' + escHtml(t.title) + (producerCode ? " " + escHtml(producerCode) : "") + (fullName ? " " + escHtml(fullName) : "") + '</title><style>';
  h += '@page{size:letter;margin:0.5in 0.6in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10.5px;color:#1a1a1a;line-height:1.4}';
  h += '.page{max-width:720px;margin:0 auto;padding:6px 0}';
  h += '.header{display:flex;justify-content:space-between;align-items:flex-end;padding-bottom:14px;border-bottom:3px solid ' + NAVY + ';margin-bottom:18px}';
  h += '.brand{font-family:"JetBrains Mono",monospace;font-size:22px;font-weight:700;color:' + NAVY + ';letter-spacing:3px}';
  h += '.brand-sub{font-size:9px;color:#666;letter-spacing:2px;text-transform:uppercase;margin-top:3px}';
  h += '.doc-title{font-size:18px;font-weight:300;color:' + NAVY + ';text-align:right;line-height:1.1;letter-spacing:1px}';
  h += '.doc-sub{font-size:10px;color:#666;text-align:right;margin-top:4px;font-family:"Courier New",monospace}';
  h += '.section{margin-top:18px}';
  h += '.section-title{font-size:11px;text-transform:uppercase;letter-spacing:1.2px;color:' + NAVY + ';font-weight:700;margin-bottom:6px;padding-bottom:3px;border-bottom:2px solid ' + NAVY + '}';
  h += '.grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px 14px;font-size:10px}';
  h += '.grid .label{font-size:8.5px;text-transform:uppercase;letter-spacing:0.5px;color:#666;font-weight:600;margin-bottom:1px}';
  h += '.grid .value{font-weight:600;color:#1a1a1a;font-size:11px}';
  h += 'table{width:100%;border-collapse:collapse;font-size:10px}';
  h += 'table th{background:#f3f4f6;text-align:left;padding:6px 8px;font-weight:700;font-size:9px;text-transform:uppercase;letter-spacing:0.4px;color:#444;border-bottom:1.5px solid #d1d5db}';
  h += 'table td{padding:6px 8px;border-bottom:1px solid #e5e7eb;vertical-align:top}';
  h += 'table tr:last-child td{border-bottom:none}';
  h += '.pill{display:inline-block;padding:2px 8px;border-radius:10px;font-size:9px;font-weight:700;letter-spacing:0.3px}';
  h += '.empty{padding:14px;text-align:center;color:#888;font-style:italic;font-size:10px;border:1px dashed #d1d5db;border-radius:4px}';
  h += '.footer{margin-top:24px;padding-top:8px;border-top:1px solid #d1d5db;font-size:9px;color:#888;text-align:center}';
  h += '@media print{body{padding:0}.no-print{display:none}}';
  h += '</style></head><body><div class="page">';

  /* Header */
  h += '<div class="header">';
  h += '<div><div class="brand">GFI · NEXUS</div><div class="brand-sub">Ground Fresh International</div></div>';
  h += '<div><div class="doc-title">' + escHtml(t.title) + '</div><div class="doc-sub">' + escHtml(fullName || "—") + '   ·   ' + escHtml(producerCode || "—") + '</div></div>';
  h += '</div>';

  /* Identity */
  h += '<div class="section"><div class="section-title">' + escHtml(t.sec_identity) + '</div>';
  h += '<div class="grid">';
  h += '<div><div class="label">' + escHtml(t.field_name) + '</div><div class="value">' + escHtml(fullName || "—") + '</div></div>';
  h += '<div><div class="label">' + escHtml(t.field_personType) + '</div><div class="value">' + escHtml(grower && grower.personType === "moral" ? t.moral : t.fisica) + '</div></div>';
  h += '<div><div class="label">' + escHtml(t.field_code) + '</div><div class="value" style="font-family:\'JetBrains Mono\',monospace;letter-spacing:2px">' + escHtml(producerCode || "—") + '</div></div>';
  h += '<div><div class="label">' + escHtml(t.field_status) + '</div><div class="value">' + escHtml(statusLabel) + '</div></div>';
  h += '</div>';
  h += '<div class="grid" style="margin-top:8px;grid-template-columns:1fr 1fr 1fr">';
  h += '<div><div class="label">' + escHtml(t.field_shipper) + '</div><div class="value">' + escHtml(shipperName || "—") + '</div></div>';
  h += '<div><div class="label">' + escHtml(t.field_phone) + '</div><div class="value">' + escHtml(phoneStr || "—") + '</div></div>';
  h += '<div><div class="label">' + escHtml(t.field_email) + '</div><div class="value">' + escHtml((grower && grower.email) || "—") + '</div></div>';
  h += '</div></div>';

  /* Season */
  h += '<div class="section"><div class="section-title">' + escHtml(t.sec_season) + '</div>';
  h += '<div style="font-size:14px;font-weight:700;font-family:\'JetBrains Mono\',monospace;color:' + NAVY + '">' + escHtml(seasonText(grower) || "—") + '</div>';
  h += '</div>';

  /* Products */
  h += '<div class="section"><div class="section-title">' + escHtml(t.sec_products) + '</div>';
  if (!grower || !grower.products || grower.products.length === 0) {
    h += '<div class="empty">' + escHtml(t.noProducts) + '</div>';
  } else {
    h += '<table><thead><tr><th>' + escHtml(t.col_product) + '</th><th>' + escHtml(t.col_category) + '</th><th style="text-align:center">' + escHtml(t.col_compliance) + '</th></tr></thead><tbody>';
    grower.products.forEach(function (p) {
      var isOg = p.category === "Organic";
      var ok = isOg ? ogOk : cvOk;
      var pillStyle = ok ? pillGreen : pillRed;
      var pillLabel = ok ? ("✓ " + t.ok) : ("● " + t.missing);
      h += '<tr><td>' + escHtml(p.product || "—") + '</td><td>' + escHtml(isOg ? t.organic : t.conventional) + '</td>';
      h += '<td style="text-align:center"><span class="pill" style="' + pillStyle + '">' + escHtml(pillLabel) + '</span></td></tr>';
    });
    h += '</tbody></table>';
  }
  h += '</div>';

  /* Ranchos */
  h += '<div class="section"><div class="section-title">' + escHtml(t.sec_ranchos) + '</div>';
  if (!grower || !grower.ranchos || grower.ranchos.length === 0) {
    h += '<div class="empty">' + escHtml(t.noRanchos) + '</div>';
  } else {
    h += '<table><thead><tr><th>' + escHtml(t.col_rancho) + '</th><th>' + escHtml(t.col_address) + '</th><th style="text-align:right;width:90px">' + escHtml(t.col_hectares) + '</th></tr></thead><tbody>';
    grower.ranchos.forEach(function (r) {
      h += '<tr><td style="font-weight:600">' + escHtml(r.name || "—") + '</td>';
      h += '<td>' + escHtml(fullAddress(r) || "—") + (r.notes ? '<div style="font-size:9px;color:#666;margin-top:2px;font-style:italic">' + escHtml(r.notes) + '</div>' : "") + '</td>';
      h += '<td style="text-align:right;font-family:\'JetBrains Mono\',monospace">' + escHtml(r.hectares || "—") + '</td></tr>';
    });
    h += '</tbody></table>';
  }
  h += '</div>';

  /* Required certs */
  h += '<div class="section"><div class="section-title">' + escHtml(t.sec_certs) + '</div>';
  h += '<table><thead><tr><th>' + escHtml(t.col_cert) + '</th><th style="text-align:center;width:120px">' + escHtml(t.col_compliance) + '</th></tr></thead><tbody>';
  required.forEach(function (item) {
    var ok = !!compliance[item.key];
    var pillStyle = ok ? pillGreen : pillRed;
    var pillLabel = ok ? ("✓ " + t.ok) : ("● " + t.missing);
    h += '<tr><td>' + escHtml(item.label) + '</td><td style="text-align:center"><span class="pill" style="' + pillStyle + '">' + escHtml(pillLabel) + '</span></td></tr>';
  });
  h += '</tbody></table></div>';

  /* Uploaded docs */
  h += '<div class="section"><div class="section-title">' + escHtml(t.sec_docs) + '</div>';
  if (docs.length === 0) {
    h += '<div class="empty">' + escHtml(t.noDocs) + '</div>';
  } else {
    /* FIX 51 — Operation Type column. FIX 55 — Commodity column. */
    /* 2026-09-28 (Arturo: "no necesito ver el nombre del archivo en
       el reporte de auditoria, agregaste una columna que dice file,
       es innecesaria") — File column removed. */
    h += '<table><thead><tr><th>' + escHtml(t.col_cert) + '</th><th>' + escHtml(t.col_certNumber) + '</th><th>' + escHtml(t.col_certifier) + '</th><th>' + escHtml(t.col_operation) + '</th><th>' + escHtml(t.col_commodity) + '</th><th>' + escHtml(t.col_docType) + '</th><th>' + escHtml(t.col_labTestId) + '</th><th>' + escHtml(t.col_sampleDate) + '</th></tr></thead><tbody>';
    docs.forEach(function (d) {
      h += '<tr><td>' + escHtml(d.certType || "—") + '</td><td style="font-family:\'JetBrains Mono\',monospace">' + escHtml(d.certNumber || "—") + '</td>';
      h += '<td>' + escHtml(d.certifier || "—") + '</td><td>' + escHtml(d.operationType || "—") + '</td><td>' + escHtml(d.commodities || "—") + '</td><td>' + escHtml(d.docType || "—") + '</td><td style="font-family:\'JetBrains Mono\',monospace">' + escHtml(d.labTestId || "—") + '</td><td style="font-family:\'JetBrains Mono\',monospace">' + escHtml(d.sampleDate ? fmtDateShort(d.sampleDate) : "—") + '</td></tr>';
    });
    h += '</tbody></table>';
  }
  h += '</div>';

  /* Footer */
  h += '<div class="footer">' + escHtml(t.generated) + ': ' + escHtml(nowStr) + '   ·   Ground Fresh International Inc.</div>';

  h += '</div></body></html>';
  return h;
}

/* ============================================================================
 * Preview / print: opens a new tab with the HTML. When `print` is true,
 * triggers the browser print dialog after a short delay so the layout
 * has time to settle. Returns the opened window for further control.
 * ============================================================================ */
export function openGrowerAuditPreview(grower, ctx, opts) {
  var w = window.open("", "_blank");
  if (!w) {
    /* Popup blocked — surface a hint so the user knows what happened. */
    try { window.alert("Pop-up blocked. Allow pop-ups for this site to preview the audit report."); } catch (e) {}
    return null;
  }
  var html = buildGrowerAuditHTML(grower, ctx);
  w.document.open();
  w.document.write(html);
  w.document.close();
  if (opts && opts.print) {
    setTimeout(function () {
      try { w.focus(); w.print(); } catch (e) {}
    }, 500);
  }
  return w;
}

/* 2026-09-28 (Arturo: "al imprimir un grower audit report quiero
   descargarlo a la carpeta de descargas del navegador ya con un
   nombre con este formato... no quiero tener que seleccionar
   carpeta") — the "PDF" button used to just call window.print() on
   the preview tab, which opens the browser's native print dialog
   (pick "Save as PDF", then choose a folder/filename by hand). This
   instead renders a REAL PDF (same html2canvas → jsPDF pipeline the
   rest of the app's documents already use, via htmlToPdfBlob) and
   triggers a direct browser download — same Blob-URL + <a download>
   pattern as protectionReport.js's Download button — which every
   browser saves straight to the Downloads folder with no dialog at
   all. Filename deliberately NOT run through the app's usual
   safeFilename() sanitizer (that replaces spaces with underscores) —
   Arturo's requested format keeps real spaces and the em dash, only
   characters that are actually illegal in a filename get stripped. */
function auditReportFilename(grower) {
  var name = "Audit Report — Grower " + (growerFullName(grower) || "");
  return name.replace(/[\\/:*?"<>|]+/g, "").trim() + ".pdf";
}
export async function downloadGrowerAuditPdf(grower, ctx) {
  var html = buildGrowerAuditHTML(grower, ctx);
  var blob = await htmlToPdfBlob(html);
  var url = URL.createObjectURL(blob);
  var a = document.createElement("a");
  a.href = url;
  a.download = auditReportFilename(grower);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ============================================================================
 * Excel export: one workbook with three sheets — Summary,
 * Products + Ranchos, Documents. The shared downloadXlsx helper
 * standardizes the filename + formatting across the app.
 * ============================================================================ */
export function downloadGrowerAuditXLSX(grower, ctx) {
  ctx = ctx || {};
  var certifications = ctx.certifications || [];
  var supplier = ctx.supplier || null;
  var lang = ctx.lang === "en" ? "en" : "es";
  var ES = lang === "es";

  var compliance = evaluateCompliance(grower, certifications);
  var required = requiredCertsFor(grower, lang);
  var docs = uploadedDocsForGrower(grower, certifications);

  var phoneStr = [grower && grower.phoneCountry ? "+" + grower.phoneCountry : "", grower && grower.phoneArea, grower && grower.phoneNumber].filter(Boolean).join(" ");
  var fullName = growerFullName(grower);
  var producerCode = (grower && grower.producerCode) || "";
  var shipperName = supplier ? (supplier.shortName || supplier.fullName || "") : "";

  /* Sheet 1 — Summary (identity + cert status flags). */
  var summaryRow = {};
  summaryRow[ES ? "Productor"        : "Grower"]         = fullName;
  summaryRow[ES ? "Código"           : "Producer code"]  = producerCode;
  summaryRow[ES ? "Exportador"       : "Linked shipper"] = shipperName;
  summaryRow[ES ? "Temporada"        : "Season"]         = seasonText(grower);
  summaryRow[ES ? "Tipo"             : "Person type"]    = grower && grower.personType === "moral" ? (ES ? "Persona Moral" : "Business") : (ES ? "Persona Física" : "Individual");
  summaryRow[ES ? "Teléfono"         : "Phone"]          = phoneStr;
  summaryRow[ES ? "Correo"           : "Email"]          = (grower && grower.email) || "";
  summaryRow[ES ? "Estado"           : "Status"]         = (grower && grower.isActive !== false) ? (ES ? "Activo" : "Active") : (ES ? "Inactivo" : "Inactive");
  required.forEach(function (item) {
    var labelKey = (ES ? "Cumple: " : "Has: ") + item.label;
    summaryRow[labelKey] = compliance[item.key] ? (ES ? "SÍ" : "YES") : "";
  });

  /* Sheet 2 — Products. */
  var cvOk = compliance.safety && compliance.audit && compliance.corrective && compliance.fsma;
  var ogOk = cvOk && compliance.organic;
  var productsRows = (grower && grower.products || []).map(function (p) {
    var isOg = p.category === "Organic";
    var ok = isOg ? ogOk : cvOk;
    var row = {};
    row[ES ? "Producto"     : "Product"]     = xs(p.product);
    row[ES ? "Categoría"    : "Category"]    = isOg ? (ES ? "Orgánico" : "Organic") : (ES ? "Convencional" : "Conventional");
    row[ES ? "Cumplimiento" : "Compliance"]  = ok ? (ES ? "OK" : "OK") : (ES ? "FALTA" : "MISSING");
    return row;
  });

  /* Sheet 3 — Ranchos. */
  var ranchosRows = (grower && grower.ranchos || []).map(function (r) {
    var row = {};
    row[ES ? "Predio"        : "Rancho"]      = xs(r.name);
    row[ES ? "Calle"         : "Street"]      = xs(r.street || r.address);
    row[ES ? "Municipio"     : "Municipality"] = xs(r.municipio);
    row[ES ? "Ciudad"        : "City"]        = xs(r.city);
    row[ES ? "Estado"        : "State"]       = xs(r.state);
    row[ES ? "C.P."          : "Zip"]         = xs(r.zip);
    row[ES ? "País"          : "Country"]     = xs(r.country);
    row[ES ? "Hectáreas"     : "Hectares"]    = num2(r.hectares);
    row[ES ? "Notas"         : "Notes"]       = xs(r.notes);
    return row;
  });

  /* Sheet 4 — Documents. */
  var docsRows = docs.map(function (d) {
    var row = {};
    row[ES ? "Cert"             : "Cert"]          = xs(d.certType);
    row[ES ? "No. Cert"         : "Cert #"]        = xs(d.certNumber);
    row[ES ? "Certificadora"    : "Certifier"]     = xs(d.certifier);
    row[ES ? "Tipo de operación": "Operation"]     = xs(d.operationType);
    row[ES ? "Commodity"        : "Commodity"]     = xs(d.commodities);
    row[ES ? "Tipo doc"         : "Doc type"]      = xs(d.docType);
    row[ES ? "No. ID Lab Test"  : "Lab Test ID#"]  = xs(d.labTestId);
    row[ES ? "Fecha de Muestreo": "Sample Date"]   = xs(d.sampleDate);
    return row;
  });

  /* Sheet 5 — Required cert status (one row per item for filtering). */
  var statusRows = required.map(function (item) {
    var row = {};
    row[ES ? "Certificación" : "Certification"] = item.label;
    row[ES ? "Estado"        : "Status"]        = compliance[item.key] ? "OK" : (ES ? "FALTA" : "MISSING");
    return row;
  });

  /* The downloadXlsx helper builds GFI_Grower_Audit_<date>.xlsx. */
  var sheets = {};
  var summarySheetName = ES ? "Resumen" : "Summary";
  sheets[summarySheetName] = [summaryRow];
  var productsSheetName = ES ? "Productos" : "Products";
  sheets[productsSheetName] = productsRows.length ? productsRows : [{}];
  var ranchosSheetName = ES ? "Ranchos" : "Ranchos";
  sheets[ranchosSheetName] = ranchosRows.length ? ranchosRows : [{}];
  var statusSheetName = ES ? "Cumplimiento" : "Compliance";
  sheets[statusSheetName] = statusRows;
  var docsSheetName = ES ? "Documentos" : "Documents";
  sheets[docsSheetName] = docsRows.length ? docsRows : [{}];

  var fileBase = "Grower_Audit_" + (producerCode || "X") + "_" + (fullName.replace(/[^A-Za-z0-9_-]+/g, "_").substring(0, 30) || "grower");
  downloadXlsx(fileBase, "Detail", sheets);
}
